import * as React from "react"

import { cn } from "@/lib/utils"

import type { LiveState } from "../data/review-data"

/**
 * Three-state liveness dot. Every state renders distinctly and carries a
 * tooltip naming the consequence.
 *
 * Spec §"realtime" calls out that the current app's "Live" indicator
 * only reflects socket open — a socket can be open while broadcasts
 * are silently dropped upstream. Three states keep the UI honest:
 * `live`, `reconnecting`, and `offline` are shown as three different
 * things, not a green dot with wishful thinking behind it.
 */

const STATE_TONE: Record<LiveState, string> = {
  live: "bg-master",
  reconnecting: "bg-print",
  offline: "bg-text-3",
}

const STATE_LABEL: Record<LiveState, string> = {
  live: "Live",
  reconnecting: "Reconnecting",
  offline: "Offline",
}

const STATE_HINT: Record<LiveState, string> = {
  live: "Updates from teammates appear within a few seconds.",
  reconnecting:
    "Lost the live connection — trying again. Findings stay editable; changes may not appear for teammates until reconnect.",
  offline: "No live connection. Showing the last-fetched snapshot.",
}

export interface ReviewLiveIndicatorProps {
  state: LiveState
  className?: string
}

export function ReviewLiveIndicator({
  state,
  className,
}: ReviewLiveIndicatorProps): JSX.Element {
  return (
    <span
      className={cn("inline-flex items-center gap-1.5", className)}
      title={STATE_HINT[state]}
    >
      <span
        aria-hidden="true"
        className={cn(
          "relative inline-flex size-2 rounded-full",
          STATE_TONE[state],
        )}
      >
        {state === "live" && (
          <span className="absolute inset-0 animate-ping rounded-full bg-master/60 motion-reduce:animate-none" />
        )}
      </span>
      <span className="text-caption font-medium text-text-2">
        {STATE_LABEL[state]}
      </span>
    </span>
  )
}
